import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categoryArticles } from '@/mocks/data';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = categoryArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = categoryArticles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 太阳娱乐精品文章 | 太阳娱乐官网`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',太阳娱乐'}
        ogType="article"
        articlePublishedTime={article.date}
        canonicalUrl={`https://hznaweisi.cn/articles/${article.slug}`}
      />
      <SchemaOrg type="Article" data={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        url: `https://hznaweisi.cn/articles/${article.slug}`,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          '@type': 'Organization',
          name: '太阳娱乐',
        },
        publisher: {
          '@type': 'Organization',
          name: '太阳娱乐',
          logo: {
            '@type': 'ImageObject',
            url: 'https://hznaweisi.cn/logo.png',
          },
        },
        image: article.image,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb />

          <article className="py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <header className="mb-8">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}阅读</span>
                  <span>·</span>
                  <span>{article.views.toLocaleString()} 浏览</span>
                </div>
              </header>

              <div className="mb-10 rounded-xl overflow-hidden aspect-[16/9]">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_p]:mb-4 [&_p]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/articles/${ra.slug}`}
                      className="group block bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative overflow-hidden aspect-[16/10]">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <div className="text-xs text-foreground-500 mb-2">{ra.date}</div>
                        <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}